// Weather service for fetching and caching weather data

import apiService from './apiService';

class WeatherService {
  constructor() {
    this.cache = new Map();
    this.cacheTTL = 15 * 60 * 1000; // 15 minutes
    this.pendingRequests = new Map();
  }

  // Get weather for multiple locations
  async getWeatherForLocations(locations) {
    if (!locations || locations.length === 0) {
      return [];
    }

    // Filter out locations that are already cached and still valid
    const now = Date.now();
    const locationsToFetch = [];
    const cachedWeather = [];

    for (const location of locations) {
      const cacheKey = this.getCacheKey(location.latitude, location.longitude);
      const cached = this.cache.get(cacheKey);
      
      if (cached && now - cached.timestamp < this.cacheTTL) {
        // Use cached data
        cachedWeather.push({
          ...cached.data,
          id: location.id
        });
      } else {
        // Need to fetch
        locationsToFetch.push(location);
      }
    }

    // If all data is cached, return it
    if (locationsToFetch.length === 0) {
      return cachedWeather;
    }

    // Create cache key for the request
    const requestKey = locationsToFetch.map(l => this.getCacheKey(l.latitude, l.longitude)).join('|');
    
    // Check if we have a pending request for this set
    if (this.pendingRequests.has(requestKey)) {
      const fetchedWeather = await this.pendingRequests.get(requestKey);
      return [...cachedWeather, ...fetchedWeather];
    }

    // Make the API request
    const request = this.fetchWeatherData(locationsToFetch);
    this.pendingRequests.set(requestKey, request);

    try {
      const fetchedWeather = await request;
      
      // Cache the new data
      fetchedWeather.forEach(weather => {
        if (weather.success) {
          const location = locationsToFetch.find(l => l.id === weather.id);
          if (location) {
            const cacheKey = this.getCacheKey(location.latitude, location.longitude);
            this.cache.set(cacheKey, {
              data: weather,
              timestamp: now
            });
          }
        }
      });

      return [...cachedWeather, ...fetchedWeather];
    } finally {
      this.pendingRequests.delete(requestKey);
    }
  }

  async fetchWeatherData(locations) {
    try {
      const response = await apiService.getWeather(locations);
      return response.weather || [];
    } catch (error) {
      console.error('Weather API error:', error);
      // Return failed entries for each location
      return locations.map(location => ({
        id: location.id,
        success: false,
        last_updated: new Date().toISOString()
      }));
    }
  }

  getCacheKey(lat, lng) {
    // Round to reasonable precision for caching
    const precision = 10000; // 4 decimal places
    const roundedLat = Math.round(lat * precision) / precision;
    const roundedLng = Math.round(lng * precision) / precision;
    return `${roundedLat},${roundedLng}`;
  }

  // Convert temperature to display format
  formatTemperature(tempF) {
    return `${Math.round(tempF)}°F`;
  }

  // Map weather icon codes to Material UI icons
  getIconForCondition(iconCode, isNight = false) {
    switch (iconCode) {
      case 'sunny':
        return 'WbSunny';
      case 'clear_night':
        return 'NightlightRound';
      case 'partly_cloudy':
        return isNight ? 'CloudQueue' : 'CloudQueue';
      case 'cloudy':
        return 'Cloud';
      case 'cloudy_night':
        return 'CloudQueue';
      case 'rain':
        return 'WaterDrop';
      case 'drizzle':
        return 'Grain';
      case 'thunderstorm':
        return 'FlashOn';
      case 'snow':
        return 'AcUnit';
      case 'fog':
        return 'Visibility';
      default:
        return 'Help'; // Unknown weather
    }
  }

  // Format wind direction
  getWindDirection(degrees) {
    const directions = ['N', 'NNE', 'NE', 'ENE', 'E', 'ESE', 'SE', 'SSE', 'S', 'SSW', 'SW', 'WSW', 'W', 'WNW', 'NW', 'NNW'];
    const index = Math.round(degrees / 22.5) % 16;
    return directions[index];
  }

  // Clear cache
  clearCache() {
    this.cache.clear();
    this.pendingRequests.clear();
  }

  // Get cache stats
  getCacheStats() {
    return {
      size: this.cache.size,
      pendingRequests: this.pendingRequests.size
    };
  }
}

// Export singleton instance
export const weatherService = new WeatherService();
export default weatherService;